import Close from "@mui/icons-material/Close";
import FormSectionCompleteIcon from "@mui/icons-material/Done";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { useNavigate } from "react-router-dom";

import { dayjs } from "@thalamos/common";
import type {
  IncidentWorkItemWithContext,
  RecordPolicePresenceMinimumFields,
} from "@thalamos/vision-common";
import { getWorkflowTranslationContext, tk } from "@thalamos/vision-common";

import { PopupDialog } from "../../components/Dialog/PopupDialog.js";
import { renderUkDateTime } from "../../components/util.js";
import { getPolicePresenceResponseText } from "../../pages/PolicePresenceLogPage/PolicePresenceLogPage.js";
import { createRoute } from "../../routing/createRoute.js";

export function ReviewBeforeEndingModal({
  open,
  onClose,
  onContinue,
  latestPlaceOfSafetyName,
  latestPolicePresenceName,
  latestPolicePresenceUpdatedAt,
  incidentWorkItem,

  canEnterOfficerRequestInformation,
  navigate,
}: {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
  latestPlaceOfSafetyName: string | undefined;
  latestPolicePresenceName?: RecordPolicePresenceMinimumFields["hasRemainedAtPlaceOfSafety"]["value"];
  latestPolicePresenceUpdatedAt?: Date;
  incidentWorkItem: IncidentWorkItemWithContext | undefined;
  canEnterOfficerRequestInformation: boolean;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const { t } = useTranslation();
  const translationOptions = {
    context: getWorkflowTranslationContext({ incidentWorkItem }),
  };

  return (
    <PopupDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      disableDialogRootPadding={false}
      testId="review-before-ending-dialog"
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          "& .MuiTypography-root": {
            fontSize: "1rem",
            fontWeight: 700,
            color: "text.primary",
          },
        }}
      >
        {t("reviewBeforeEnding.title")}
        <IconButton
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.secondary",
            "&:hover": { backgroundColor: "transparent" },
          }}
          aria-label={t("common.close")}
          onClick={onClose}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("reviewBeforeEnding.introText")}
        </Typography>

        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("reviewBeforeEnding.ensureDetails")}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(
                  "reviewBeforeEnding.latestPlaceOfSafetyLabel",
                  translationOptions,
                )}
              </Typography>
              <Typography variant="body1">
                {latestPlaceOfSafetyName ??
                  t(tk("reviewBeforeEnding.dataMissing"))}
              </Typography>
            </Box>

            <Typography
              variant="body2"
              color="primary"
              sx={{
                fontWeight: 600,
                mt: 0.5,
                cursor: "pointer",
                whiteSpace: "nowrap",
                alignSelf: "flex-start",
              }}
              onClick={() => {
                if (!incidentWorkItem) return;
                if (!latestPlaceOfSafetyName) {
                  navigate(
                    createRoute.incidentRecordPlaceOfSafetyPage(
                      incidentWorkItem.incidentWorkItem.id,
                    ),
                  );
                } else {
                  navigate(
                    createRoute.incidentReviewPlacesOfSafetyPage(
                      incidentWorkItem.incidentWorkItem.id,
                    ),
                  );
                }
              }}
            >
              {t("reviewBeforeEnding.update")}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        {canEnterOfficerRequestInformation && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t("reviewBeforeEnding.latestPolicePresenceEntryLabel")}
                </Typography>
                <Typography variant="body1">
                  {getPolicePresenceResponseText(latestPolicePresenceName)}
                </Typography>
                {latestPolicePresenceUpdatedAt && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      mt: 1,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <FormSectionCompleteIcon
                      sx={{ color: "success.main", fontSize: "1rem" }}
                    />
                    {t("reviewBeforeEnding.lastUpdated")}{" "}
                    {renderUkDateTime(
                      dayjs.utc(latestPolicePresenceUpdatedAt).toDate(),
                      true,
                    )}
                  </Typography>
                )}
              </Box>

              <Typography
                variant="body2"
                color="primary"
                sx={{
                  fontWeight: 600,
                  mt: 0.5,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  alignSelf: "flex-start",
                }}
                onClick={() => {
                  if (incidentWorkItem !== undefined) {
                    navigate(
                      createRoute.incidentPolicePresenceLogPage(
                        incidentWorkItem.incidentWorkItem.id,
                      ),
                    );
                  }
                }}
              >
                {t("reviewBeforeEnding.update")}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={onContinue}
          sx={{ borderRadius: 2, width: "100%" }}
        >
          {t("reviewBeforeEnding.continue")}
        </Button>
      </DialogActions>
    </PopupDialog>
  );
}
